// Debug log: structured browser-side events plus mirrored server events.
//
// - Bounded ring of recent events (DEBUG_EVENT_CAP).
// - Top-level string fields are truncated to MAX_DETAIL_CHARS.
// - The render mode is "clean" (one-line summaries) or "full" (raw JSON).

import { DEBUG_EVENT_CAP } from "./constants.js";
import { els } from "./dom.js";

const MAX_DETAIL_CHARS = 8 * 1024;
const MAX_STRINGIFY_CHARS = 20000;

export const debugState = {
  events: 0,
  errors: 0,
  audioInChunks: 0,
  audioInBytes: 0,
  imageFrames: 0,
  imageBytes: 0,
  audioOutChunks: 0,
  audioOutBytes: 0,
  imageDropped: 0,
  audioDropped: 0,
  lastMicLogAt: 0,
  lastOutLogAt: 0,
  rawEvents: [],
  nextEventId: 1,
};

// Subscribers that observe every emitted debug event in real time. Used by
// the local-only recorder to capture a debug log alongside its media without
// being limited by `DEBUG_EVENT_CAP` (which only bounds the on-screen ring).
// Listeners are delivered the same event object that goes into `rawEvents`,
// so any redaction/bounding already applied is preserved.
const debugListeners = new Set();

export function addDebugEventListener(fn) {
  if (typeof fn !== "function") {
    throw new TypeError("addDebugEventListener: listener must be a function");
  }
  debugListeners.add(fn);
  return () => {
    debugListeners.delete(fn);
  };
}

// ---- formatting --------------------------------------------------------------

export function formatBytes(bytes) {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function compactText(text, maxLen = 220) {
  const s = String(text || "")
    .replace(/\s+/g, " ")
    .trim();
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen)}…`;
}

function safeStringify(value, maxLen = MAX_STRINGIFY_CHARS) {
  let s;
  try {
    s = JSON.stringify(value, null, 2);
  } catch (_) {
    s = String(value);
  }
  if (s.length > maxLen) return `${s.slice(0, maxLen)}\n... truncated ...`;
  return s;
}

// Bound the top-level string fields of `detail` so one giant payload from
// the server can't dominate memory.
function boundDetail(detail) {
  if (!detail || typeof detail !== "object") return detail;
  const out = {};
  for (const [k, v] of Object.entries(detail)) {
    if (typeof v === "string" && v.length > MAX_DETAIL_CHARS) {
      out[k] = `${v.slice(0, MAX_DETAIL_CHARS)}... <truncated ${v.length - MAX_DETAIL_CHARS} chars>`;
    } else {
      out[k] = v;
    }
  }
  return out;
}

// ---- counters ---------------------------------------------------------------

export function updateDebugCounters() {
  if (!els.dbgAudioIn) return;
  els.dbgAudioIn.textContent = `${debugState.audioInChunks} / ${formatBytes(debugState.audioInBytes)}`;
  els.dbgImages.textContent = `${debugState.imageFrames} / ${formatBytes(debugState.imageBytes)}`;
  els.dbgAudioOut.textContent = `${debugState.audioOutChunks} / ${formatBytes(debugState.audioOutBytes)}`;
  els.dbgErrors.textContent = String(debugState.errors);
}

export function setDebugWsState(state) {
  if (els.dbgWs) els.dbgWs.textContent = state;
}

// ---- "what the model sees" previews -----------------------------------------
//
// These render the exact JPEG bytes that are dispatched to the model so an
// operator can verify framing, cropping, and (for composite workflows) the
// merged top/bottom layout. They are best-effort and guard on element
// presence so they no-op in tests and in API-only/headless builds.

function _frameDataUrl(frame) {
  if (!frame || !frame.data) return null;
  const mime = frame.mime_type || "image/jpeg";
  return `data:${mime};base64,${frame.data}`;
}

// The latest frame actually sent to the model (any workflow / observer mode).
export function setDebugCurrentView(frame) {
  if (!els.debugCurrentView) return;
  const url = _frameDataUrl(frame);
  if (!url) return;
  els.debugCurrentView.src = url;
  els.debugCurrentView.hidden = false;
  if (els.debugCurrentViewEmpty) els.debugCurrentViewEmpty.hidden = true;
}

// The live composite frame for composite-vertical workflows (Hellmann Expert).
// Updated every time a composite frame is produced, even if a later backpressure
// drop means that particular frame was not sent — this is the "video sent to
// the model" preview.
export function setDebugCompositeView(frame) {
  if (!els.debugCompositeView) return;
  const url = _frameDataUrl(frame);
  if (!url) return;
  els.debugCompositeView.src = url;
  els.debugCompositeView.hidden = false;
}

// Show/hide the composite preview block. Composite mode is the only one that
// merges multiple sources, so the block is hidden for ordinary workflows.
export function setCompositeViewActive(active) {
  if (els.debugCompositeViewWrap) els.debugCompositeViewWrap.hidden = !active;
}

// ---- filtering --------------------------------------------------------------

function isMediaEvent(name) {
  return (
    name.includes("audio") ||
    name.includes("image") ||
    name.includes("video") ||
    name.includes("media")
  );
}

function isEmptyObject(value) {
  return (
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0
  );
}

function isResponseAudioUsage(detail) {
  const details = detail && detail.response_tokens_details;
  return (
    detail &&
    typeof detail === "object" &&
    detail.response_token_count !== undefined &&
    Array.isArray(details) &&
    details.length > 0 &&
    details.every((d) => d.modality === "AUDIO") &&
    detail.prompt_token_count === undefined &&
    detail.thoughts_token_count === undefined
  );
}

function isLowValueModelEvent(detail) {
  if (!detail || typeof detail !== "object") return false;
  const keys = Object.keys(detail);
  if (keys.length === 0) return true;
  if (keys.length === 1 && keys[0] === "session_resumption_update") return true;
  if (keys.length === 1 && keys[0] === "usage_metadata") return true;
  if (keys.length === 1 && keys[0] === "generation_complete") return true;
  if (keys.length === 1 && keys[0] === "output_transcription") return true;
  if (
    detail.turn_complete === true &&
    (detail.usage_metadata === undefined || isEmptyObject(detail.usage_metadata))
  ) {
    return true;
  }
  return false;
}

export function shouldShowDebugEvent(event) {
  const mode = els.debugMode ? els.debugMode.value : "clean";
  if (mode === "full") return true;
  const { name, detail } = event;
  if (isMediaEvent(name) && !els.debugMedia?.checked) return false;
  if (name === "session_resumption_update") return false;
  if (name === "model_event" && isLowValueModelEvent(detail)) return false;
  if (name === "model_event") return false;
  if (
    name === "server_content" &&
    detail &&
    Object.keys(detail).length === 1 &&
    detail.output_transcription
  ) {
    return false;
  }
  if (name === "usage_metadata" && isResponseAudioUsage(detail)) return false;
  if (name === "usage_metadata" && isEmptyObject(detail)) return false;
  return true;
}

// ---- summaries --------------------------------------------------------------

function formatUsageSummary(u) {
  if (!u || typeof u !== "object") return "usage metadata";
  const parts = [];
  if (u.prompt_token_count !== undefined) parts.push(`prompt ${u.prompt_token_count}`);
  if (u.response_token_count !== undefined) parts.push(`response ${u.response_token_count}`);
  if (u.thoughts_token_count !== undefined) parts.push(`thoughts ${u.thoughts_token_count}`);
  if (u.total_token_count !== undefined) parts.push(`total ${u.total_token_count}`);
  const tokenDetails = u.prompt_tokens_details || u.response_tokens_details || [];
  if (Array.isArray(tokenDetails) && tokenDetails.length > 0) {
    const byModality = tokenDetails
      .map((d) => `${String(d.modality || "?").toLowerCase()} ${d.token_count}`)
      .join(", ");
    parts.push(`[${byModality}]`);
  }
  return parts.length ? `usage: ${parts.join(" · ")}` : "usage metadata";
}

export function summarizeDebugEvent(event) {
  const { source, name, detail } = event;
  switch (name) {
    case "app_loaded":
      return `app loaded · send ${detail.send_rate} Hz · receive ${detail.receive_rate} Hz · frame every ${detail.frame_interval_ms} ms`;
    case "websocket_open":
      return "websocket opened";
    case "websocket_closed":
      return "websocket closed";
    case "start":
      return `start · voice ${detail.voice} · thinking ${detail.thinking_level} · thoughts ${detail.include_thoughts ? "on" : "off"}`;
    case "session_start_requested": {
      const cfg = detail.resolved_config || {};
      return `session requested · ${cfg.model || "model"} · ${cfg.voice || "voice"} · thinking ${cfg.thinking_level || "?"} · transcripts on`;
    }
    case "gemini_session_connected":
      return `VTA connected · ${detail.model || "model"} · audio response mode`;
    case "status":
      return `status: ${detail.message}`;
    case "text":
    case "text_to_model":
      return `user text → model · ${detail.chars} chars · "${compactText(detail.text, 260)}"`;
    case "model_text":
      return `model text · ${detail.chars} chars · "${compactText(detail.text, 260)}"`;
    case "model_transcript":
      return `model transcript · ${detail.chars} chars · ${compactText(detail.text, 360)}`;
    case "user_transcript":
      return `user transcript · ${detail.chars} chars · ${compactText(detail.text, 260)}`;
    case "thought_summary":
      return `thought summary · ${detail.chars} chars · ${compactText(detail.text, 320)}`;
    case "usage_metadata":
      return formatUsageSummary(detail);
    case "server_content": {
      const flags = [];
      if (detail.interrupted) flags.push("interrupted");
      if (detail.generation_complete) flags.push("generation complete");
      if (detail.turn_complete) flags.push("turn complete");
      if (detail.input_transcription)
        flags.push(`input transcript: ${compactText(detail.input_transcription, 180)}`);
      if (detail.output_transcription)
        flags.push(`output transcript: ${compactText(detail.output_transcription, 180)}`);
      return flags.length ? flags.join(" · ") : "server content";
    }
    case "turn_complete":
      if (detail && detail.model_audio_chunks_total !== undefined) {
        return `turn complete · audio ${detail.model_audio_chunks_total} chunks · ${formatBytes(detail.model_audio_bytes_total)}`;
      }
      return "turn complete";
    case "step_signal_requested": {
      const dir = detail.direction || detail.signal_direction || detail.requested_direction || "step";
      const changed =
        detail.changed === undefined ? "" : detail.changed ? " · changed" : " · no change";
      return `${dir} step function requested${changed}`;
    }
    case "step_signal_shown":
      return `${detail.direction} step indicator shown · ${detail.duration_ms} ms`;
    case "step_signal_hidden":
      return `${detail.direction} step indicator hidden`;
    case "tool_response_to_model":
      return "tool response sent to model";
    case "interrupt_notice_requested":
      return `interrupt requested · "${compactText(detail.text, 220)}"`;
    case "interrupt_notice_shown":
      return `interrupt shown · ${detail.duration_ms} ms · "${compactText(detail.text, 220)}"`;
    case "interrupt_notice_hidden":
      return "interrupt hidden";
    case "browser_audio_summary":
    case "audio_to_model_summary":
      return `audio → model · ${detail.chunks_total} chunks · ${formatBytes(detail.bytes_total)} · ${detail.sample_rate || detail.receive_sample_rate || ""} Hz`;
    case "browser_image_frame":
    case "image_to_model":
      return `image frame → model · #${detail.frame_index} · ${formatBytes(detail.bytes || detail.approx_bytes || 0)} · ${detail.mime_type || "image"}`;
    case "image_frame_dropped":
      return `image frame dropped · #${detail.frame_index} · ${detail.reason || "backpressure"} · ws buffered ${formatBytes(detail.buffered || 0)}`;
    case "audio_chunk_dropped":
      return `audio chunk dropped · ${detail.reason || "backpressure"} · ws buffered ${formatBytes(detail.buffered || 0)}`;
    case "model_audio_summary":
      return `model audio → browser · ${detail.chunks_total} chunks · ${formatBytes(detail.bytes_total)}`;
    case "audio_capture_started":
      return "audio capture started";
    case "audio_capture_stopped":
      return "audio capture stopped";
    case "video_stopped":
      return `video stopped · ${detail.reason || ""}`;
    case "all_media_stopped":
      return "all media stopped";
    case "screen_share_started":
      return "screen share started";
    case "camera_started":
      return "camera started";
    case "video_disabled":
      return "video disabled";
    case "disconnect_requested":
      return `disconnect requested · ${detail.reason || ""}`;
    case "stop":
    case "stop_requested":
      return "stop requested";
    case "error":
    case "server_exception":
    case "websocket_error":
      return `error · ${detail.message || detail.error || safeStringify(detail, 800)}`;
    default:
      return `${source} · ${name}`;
  }
}

// ---- rendering --------------------------------------------------------------

export function appendDebugRow(event) {
  if (!els.debugLog) return;
  if (!shouldShowDebugEvent(event)) return;
  const mode = els.debugMode ? els.debugMode.value : "clean";
  const row = document.createElement("div");
  row.className = `debug-row ${event.level} ${mode}`;
  const title = document.createElement("div");
  title.className = "debug-row-title";
  const time = document.createElement("span");
  time.className = "debug-time";
  time.textContent = new Date(event.ts).toLocaleTimeString();
  const source = document.createElement("span");
  source.className = "debug-source";
  source.textContent = event.source;
  const name = document.createElement("span");
  name.className = "debug-name";
  name.textContent = event.name;
  title.appendChild(time);
  title.appendChild(source);
  title.appendChild(name);
  row.appendChild(title);

  if (mode === "full") {
    const pre = document.createElement("pre");
    pre.textContent = safeStringify({
      source: event.source,
      name: event.name,
      level: event.level,
      detail: event.detail,
    });
    row.appendChild(pre);
  } else {
    const summary = document.createElement("p");
    summary.className = "debug-summary";
    summary.textContent = summarizeDebugEvent(event);
    row.appendChild(summary);
  }

  els.debugLog.appendChild(row);
}

export function rerenderDebugLog() {
  if (!els.debugLog) return;
  els.debugLog.textContent = "";
  for (const event of debugState.rawEvents) {
    appendDebugRow(event);
  }
  if (els.debugAutoscroll && els.debugAutoscroll.checked) {
    els.debugLog.scrollTop = els.debugLog.scrollHeight;
  }
}

export function logDebug(source, name, detail = {}, level = "info") {
  const event = {
    id: debugState.nextEventId++,
    ts: Date.now(),
    source,
    name,
    detail: boundDetail(detail || {}),
    level,
  };
  // Deliver to subscribers BEFORE the cap/UI work so a slow render path
  // cannot starve a listener, and a throwing listener cannot break logging.
  for (const fn of debugListeners) {
    try {
      fn(event);
    } catch (_) {
      // Subscribers are isolated: one bad listener must not break logging.
    }
  }
  debugState.events += 1;
  if (level === "error") debugState.errors += 1;
  debugState.rawEvents.push(event);
  while (debugState.rawEvents.length > DEBUG_EVENT_CAP) {
    debugState.rawEvents.shift();
  }
  appendDebugRow(event);
  updateDebugCounters();
  if (els.debugAutoscroll && els.debugAutoscroll.checked && els.debugLog) {
    els.debugLog.scrollTop = els.debugLog.scrollHeight;
  }
}

export function clearDebugLog() {
  if (els.debugLog) els.debugLog.textContent = "";
  debugState.events = 0;
  debugState.errors = 0;
  debugState.audioInChunks = 0;
  debugState.audioInBytes = 0;
  debugState.imageFrames = 0;
  debugState.imageBytes = 0;
  debugState.audioOutChunks = 0;
  debugState.audioOutBytes = 0;
  debugState.imageDropped = 0;
  debugState.audioDropped = 0;
  debugState.rawEvents = [];
  debugState.nextEventId = 1;
  updateDebugCounters();
  logDebug("browser", "debug_cleared");
}

// ---- counters callbacks -----------------------------------------------------

import { SEND_RATE, RECV_RATE } from "./constants.js";

export function noteBrowserAudioChunk(byteLength) {
  debugState.audioInChunks += 1;
  debugState.audioInBytes += byteLength;
  updateDebugCounters();
  const now = performance.now();
  if (now - debugState.lastMicLogAt > 2000) {
    logDebug("browser -> server", "browser_audio_summary", {
      chunks_total: debugState.audioInChunks,
      bytes_total: debugState.audioInBytes,
      last_chunk_bytes: byteLength,
      sample_rate: SEND_RATE,
    });
    debugState.lastMicLogAt = now;
  }
}

export function noteBrowserImageFrame(frame) {
  debugState.imageFrames += 1;
  const approxBytes = Math.floor((frame.data.length * 3) / 4);
  debugState.imageBytes += approxBytes;
  updateDebugCounters();
  logDebug("browser -> server", "browser_image_frame", {
    frame_index: debugState.imageFrames,
    mime_type: frame.mime_type,
    approx_bytes: approxBytes,
    base64_chars: frame.data.length,
  });
}

export function noteImageFrameDropped(frameIndex, buffered, reason = "ws_buffer_full") {
  debugState.imageDropped += 1;
  logDebug(
    "browser",
    "image_frame_dropped",
    { frame_index: frameIndex, buffered, reason, dropped_total: debugState.imageDropped },
    "warn",
  );
}

export function noteAudioChunkDropped(buffered, reason = "ws_buffer_full") {
  debugState.audioDropped += 1;
  logDebug(
    "browser",
    "audio_chunk_dropped",
    { buffered, reason, dropped_total: debugState.audioDropped },
    "warn",
  );
}

export function noteModelAudioChunk(byteLength) {
  debugState.audioOutChunks += 1;
  debugState.audioOutBytes += byteLength;
  updateDebugCounters();
  const now = performance.now();
  if (now - debugState.lastOutLogAt > 2000) {
    logDebug("server -> browser", "model_audio_summary", {
      chunks_total: debugState.audioOutChunks,
      bytes_total: debugState.audioOutBytes,
      last_chunk_bytes: byteLength,
      sample_rate: RECV_RATE,
    });
    debugState.lastOutLogAt = now;
  }
}

export function handleServerDebug(event) {
  if (!event) return;
  logDebug(
    event.direction || "server",
    event.name || "debug",
    event.detail || {},
    event.level || "info",
  );
}
